import { classNames } from '@dimjs/utils';
import { hooks } from '@wove/react';
import 'intersection-observer';
import {
  CSSProperties,
  DetailedHTMLProps,
  FC,
  Fragment,
  ImgHTMLAttributes,
  ReactNode,
  SyntheticEvent,
  useState,
} from 'react';
import { InVisibleView } from '../in-visible-view';
import { Spinner } from '../spinner/spinner';
import { NativeProps } from '../utils/native-props';
import { numberPx2DPIpx, numberPx2rem } from '../utils/pxrem';
import './style.less';

export type ImageProps = {
  src: string;
  lazy?: boolean;
  threshold?: number;
  imgProps?: Omit<
    DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>,
    'src' | 'width' | 'height' | 'onClick' | 'onError' | 'onLoad'
  >;
  width?: string | number;
  height?: string | number;
  loading?: ReactNode;
  onClick?: (event: React.MouseEvent<HTMLDivElement, Event>) => void;
  onError?: (event: SyntheticEvent<HTMLImageElement, Event>) => void;
  // 图片填充模式
  fit?: CSSProperties['objectFit'];
} & NativeProps;

export const Image: FC<ImageProps> = (props) => {
  const [showImage, setShowImage] = useState(!props.lazy);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [placeholder, setPlaceholder] = useState(true);

  const onChange = hooks.useCallbackRef((inView) => {
    if (inView) {
      setShowImage(true);
    }
  });

  const style: ImageProps['style'] = {};
  style['--width'] = numberPx2rem(props.width);
  style['--height'] = numberPx2rem(props.height);

  const imgStyle = { objectFit: props.fit };

  const className = classNames(
    'v-image',
    {
      'v-image-placeholder': placeholder,
      'v-image-error': failed,
    },
    props.className,
  );

  // threshold只支持px格式
  const threshold = numberPx2DPIpx(props.threshold, '0px');

  return (
    <Fragment>
      {!showImage && (
        <InVisibleView onChange={onChange} unobserve={true} rootMargin={`0px 0px ${threshold} 0px`} />
      )}
      <div className={className} onClick={props.onClick} style={{ ...style, ...props.style }}>
        {failed && <div className="v-image-error-content" />}
        {showImage && (
          <img
            {...props.imgProps}
            style={{ ...imgStyle, ...props.imgProps?.style }}
            src={props.src}
            onLoad={() => {
              setPlaceholder(false);
              setLoaded(true);
            }}
            onError={(event) => {
              setPlaceholder(false);
              setLoaded(true);
              setFailed(true);
              props.onError?.(event);
            }}
          />
        )}
        {!placeholder && !loaded && props.loading}
      </div>
    </Fragment>
  );
};

Image.defaultProps = {
  lazy: false,
  threshold: 50,
  loading: <Spinner spinning={true} />,
};
